import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useMenuStructureNavigation } from './useMenuStructureNavigation';

export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  visible: boolean;
  order: number;
  category?: string;
  fullPath?: string; // Hierarchical label path like "Content Marketing > Long-Form Content > Upload Ideas"
  children?: NavigationItem[];
}

export interface NavigationPreferences {
  items: NavigationItem[];
  version: number;
}

export const useNavigationPreferences = () => {
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();
  const { defaultNavigationItems, mergeWithPreferences } = useMenuStructureNavigation();

  // Fetch user's navigation preferences
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['navigation-preferences', user?.id, currentBrandId],
    queryFn: async () => {
      if (!user?.id || !currentBrandId) return null;

      try {
        // Try without schema first
        let { data, error } = await supabase.rpc('get_user_navigation_preferences', {
          p_user_id: user.id,
          p_brand_id: currentBrandId
        });

        // If not found, try with explicit schema prefix
        if ((error as any)?.code === 'PGRST116' || (error as any)?.message?.includes('404')) {
          ({ data, error } = await (supabase as any).rpc('public.get_user_navigation_preferences', {
            p_user_id: user.id,
            p_brand_id: currentBrandId
          }));
        }

        if (error) throw error;
        const result = data as unknown as NavigationPreferences;
        
        // Merge with default structure for auto-population
        if (result?.items) {
          const merged = mergeWithPreferences(result.items);
          return {
            ...result,
            items: merged
          };
        }
        
        // Return default structure if no preferences exist
        return {
          items: defaultNavigationItems,
          version: 1
        };
      } catch (err: any) {
        return {
          items: defaultNavigationItems,
          version: 1
        };
      }
    },
    enabled: !!user?.id && !!currentBrandId,
  });

  // Update navigation preferences
  const updatePreferences = useMutation({
    mutationFn: async (newPreferences: NavigationPreferences) => {
      if (!user?.id || !currentBrandId) throw new Error('User or brand not available');

      const { data, error } = await supabase.rpc('update_navigation_preferences', {
        p_user_id: user.id,
        p_brand_id: currentBrandId,
        p_preferences: newPreferences as any
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['navigation-preferences', user?.id, currentBrandId] 
      });
      toast.success('Navigation preferences updated');
    },
    onError: (error) => {
      console.error('Failed to update navigation preferences:', error);
      toast.error('Failed to update navigation preferences');
    },
  });

  // Reset to role-based defaults
  const resetToDefaults = useMutation({
    mutationFn: async () => {
      if (!user?.id || !currentBrandId) throw new Error('User or brand not available');

      const { error } = await supabase
        .from('os_navigation_preferences')
        .delete()
        .eq('user_id', user.id)
        .eq('brand_id', currentBrandId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['navigation-preferences', user?.id, currentBrandId] 
      });
      toast.success('Navigation reset to defaults');
    },
    onError: (error) => {
      console.error('Failed to reset navigation preferences:', error);
      toast.error('Failed to reset navigation preferences');
    },
  });

  return {
    preferences,
    isLoading,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
    resetToDefaults: resetToDefaults.mutate,
    isResetting: resetToDefaults.isPending,
  };
};